import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";

const itineraries = [
  {
    title: "Swiss · Paris · Amsterdam",
    img: "https://images.unsplash.com/photo-1527668752968-14dc70a27c95?w=1200&q=80&auto=format",
    badge: "Editor's Pick",
    highlights: [
      "Swiss Alps & scenic train experiences",
      "Eiffel Tower & Paris city moments",
      "Amsterdam canals & nightlife",
      "Luxury stays & guided experiences",
      "Visa assistance included",
    ],
  },
  {
    title: "New Zealand",
    img: "https://images.unsplash.com/photo-1507699622108-4be3abd695ad?w=1200&q=80&auto=format",
    highlights: [
      "Queenstown adventure experiences",
      "Milford Sound scenic beauty",
      "Luxury nature retreats",
      "Curated road trip experiences",
      "Personalized travel planning",
    ],
  },
  {
    title: "Australia",
    img: "https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=1200&q=80&auto=format",
    highlights: [
      "Sydney Opera House & Harbour",
      "Gold Coast experiences",
      "Melbourne city lifestyle",
      "Great Ocean Road journeys",
      "Luxury accommodation options",
    ],
  },
  {
    title: "Dubai Luxury Escape",
    img: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1200&q=80&auto=format",
    highlights: [
      "Burj Khalifa experiences",
      "Desert safari adventures",
      "Luxury shopping destinations",
      "Yacht & nightlife experiences",
      "Premium hotel stays",
    ],
  },
  {
    title: "Bali Retreat",
    img: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=1200&q=80&auto=format",
    badge: "Honeymoon Favourite",
    highlights: [
      "Private pool villa experiences",
      "Beach clubs & sunsets",
      "Ubud nature retreats",
      "Waterfall & island tours",
      "Luxury couple getaways",
    ],
  },
  {
    title: "Thailand Premium Getaway",
    img: "https://images.unsplash.com/photo-1528181304800-259b08848526?w=1200&q=80&auto=format",
    highlights: [
      "Phuket island experiences",
      "Bangkok nightlife & shopping",
      "Luxury beach resorts",
      "Adventure water activities",
      "Curated group & couple trips",
    ],
  },
  {
    title: "Maldives Luxury Escape",
    img: "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=1200&q=80&auto=format",
    badge: "Most Romantic",
    highlights: [
      "Overwater villa stays",
      "Private island experiences",
      "Sunset cruises & water sports",
      "Luxury spa experiences",
      "Romantic couple getaways",
    ],
  },
  {
    title: "Singapore · Malaysia",
    img: "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=1200&q=80&auto=format",
    highlights: [
      "Marina Bay Sands experiences",
      "Universal Studios Singapore",
      "Kuala Lumpur city attractions",
      "Luxury shopping & dining",
      "Premium family & couple experiences",
    ],
  },
  {
    title: "USA Explorer",
    img: "https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=1200&q=80&auto=format",
    highlights: [
      "New York city experiences",
      "Las Vegas nightlife & entertainment",
      "Los Angeles & Hollywood attractions",
      "Luxury shopping destinations",
      "Premium family & group travel",
    ],
  },
];

const PHONE_TEL = "+919091100200";
const WA = "https://wa.me/919091100200";

export default function Itineraries() {
  return (
    <section id="itineraries" data-testid="itineraries-section" className="relative py-24 md:py-32 bg-white">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 -left-32 w-96 h-96 rounded-full opacity-20" style={{ background: "radial-gradient(circle, #0099CC, transparent 70%)", filter: "blur(60px)" }} />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-full opacity-15" style={{ background: "radial-gradient(circle, #D4AF37, transparent 70%)", filter: "blur(60px)" }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-gold-dark font-semibold mb-4">— International Itineraries</div>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter max-w-3xl leading-[1.05] text-navy-deep">
              Curated <span className="text-gradient-navy">International</span><br/>Experiences.
            </h2>
          </div>
          <p className="max-w-md text-slate-600 text-base leading-relaxed">
            Explore handpicked international journeys crafted for luxury, comfort, and unforgettable memories.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {itineraries.map((it, i) => (
            <motion.article
              key={it.title}
              data-testid={`itinerary-${it.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: i * 0.06, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="group relative rounded-3xl overflow-hidden bg-white border border-navy-deep/8 soft-shadow hover:premium-shadow transition-all duration-500 flex flex-col"
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={it.img}
                  alt={it.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/85 via-navy-deep/20 to-transparent" />
                {it.badge && (
                  <div className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold text-navy-deep text-[10px] uppercase tracking-[0.18em] font-bold shadow-lg">
                    <Sparkles className="w-3 h-3" strokeWidth={2.5} />
                    {it.badge}
                  </div>
                )}
                <h3 className="absolute bottom-5 left-5 right-5 font-heading text-2xl md:text-3xl font-bold leading-tight text-white drop-shadow-md">
                  {it.title}
                </h3>
              </div>

              <div className="p-6 md:p-7 flex flex-col gap-5 flex-1">
                <ul className="space-y-2.5">
                  {it.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2.5 text-[13.5px] text-slate-700 leading-snug">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 bg-gradient-to-br from-sky-brand to-gold" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto pt-4 border-t border-navy-deep/8 flex gap-2.5">
                  <a
                    href={WA}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`itinerary-cta-${i}`}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full gradient-navy text-white font-semibold text-[13px] shadow-[0_8px_24px_-10px_rgba(0,51,102,0.5)] hover:shadow-[0_14px_32px_-10px_rgba(0,51,102,0.7)] hover:scale-[1.02] transition-all"
                  >
                    Explore Itinerary
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </a>
                  <a
                    href={`tel:${PHONE_TEL}`}
                    aria-label="Call to enquire"
                    className="w-11 h-11 rounded-full bg-white border border-navy-deep/12 flex items-center justify-center hover:bg-sky-soft hover:border-sky-brand/50 transition-colors shrink-0"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4 text-navy-deep" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  </a>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
